import { useMemo, useState } from 'react';
import { allMovies } from '@/mocks/movies';
import SectionHeading from './SectionHeading';

const genres = ['全部', ...Array.from(new Set(allMovies.flatMap((movie) => movie.genres)))];

export default function GenreExplorer() {
  const [activeGenre, setActiveGenre] = useState('全部');
  const [query, setQuery] = useState('');

  const results = useMemo(() => {
    const keyword = query.trim().toLowerCase();
    return allMovies.filter((movie) => {
      const matchGenre = activeGenre === '全部' || movie.genres.includes(activeGenre);
      const matchQuery =
        keyword.length === 0 ||
        movie.title.toLowerCase().includes(keyword) ||
        movie.origin.toLowerCase().includes(keyword) ||
        movie.director.toLowerCase().includes(keyword) ||
        movie.cast.toLowerCase().includes(keyword) ||
        movie.genres.some((genre) => genre.toLowerCase().includes(keyword));
      return matchGenre && matchQuery;
    });
  }, [activeGenre, query]);

  return (
    <section id="genres" className="w-full bg-background-50 px-4 py-14 md:px-6 md:py-20">
      <div className="mx-auto max-w-6xl">
        <SectionHeading
          eyebrow="类型浏览"
          title="按类型或关键词，在本页片单里快速找片"
          desc="下面的筛选与搜索只作用于本页已收录的片单，不会跳转到其他页面。先用类型缩小范围，再用关键词定位主创或片名，几步就能找到目标。"
        />

        <div className="mt-8 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap gap-2" role="tablist" aria-label="类型筛选">
            {genres.map((genre) => {
              const active = genre === activeGenre;
              return (
                <button
                  key={genre}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setActiveGenre(genre)}
                  className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                    active
                      ? 'bg-primary-600 text-background-50'
                      : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                  }`}
                >
                  {genre}
                </button>
              );
            })}
          </div>

          <div className="relative w-full lg:w-72">
            <span className="pointer-events-none absolute left-3 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center">
              <i className="ri-search-line text-base text-foreground-500" aria-hidden="true"></i>
            </span>
            <input
              type="text"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="搜索片名、导演或演员"
              aria-label="在本页片单中搜索"
              className="w-full rounded-md border border-background-300 bg-background-50 py-2 pl-9 pr-9 text-sm text-foreground-900 placeholder:text-foreground-500 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
            />
            {query.length > 0 ? (
              <button
                type="button"
                onClick={() => setQuery('')}
                aria-label="清空搜索"
                className="absolute right-2 top-1/2 flex h-6 w-6 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full text-foreground-500 transition-colors hover:bg-background-100"
              >
                <i className="ri-close-line text-base" aria-hidden="true"></i>
              </button>
            ) : null}
          </div>
        </div>

        <p className="mt-4 text-sm text-foreground-600">
          当前条件共找到 <strong className="font-medium text-primary-700">{results.length}</strong> 部作品
          {activeGenre !== '全部' ? ` · 类型：${activeGenre}` : ''}
          {query.trim().length > 0 ? ` · 关键词：${query.trim()}` : ''}
        </p>

        {results.length > 0 ? (
          <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {results.map((movie) => (
              <article
                key={movie.id}
                className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-100 p-3 transition-colors hover:border-primary-300 hover:bg-primary-50"
              >
                <span className="hidden h-20 w-14 shrink-0 overflow-hidden rounded-md sm:block">
                  <img src={movie.poster} alt={`${movie.title} 海报`} className="h-full w-full object-cover object-top" />
                </span>
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="flex items-center gap-2">
                    <span className="truncate font-heading text-sm font-bold text-foreground-950">{movie.title}</span>
                    <span className="shrink-0 text-xs text-accent-700">{movie.score.toFixed(1)}</span>
                  </span>
                  <span className="mt-0.5 truncate text-xs text-foreground-600">
                    {movie.year} · {movie.region} · {movie.runtime}
                  </span>
                  <span className="mt-0.5 truncate text-xs text-foreground-600">导演 {movie.director}</span>
                  <span className="mt-1.5 flex flex-wrap gap-1">
                    {movie.genres.map((genre) => (
                      <span key={genre} className="rounded-full bg-background-200 px-2 py-0.5 text-[11px] text-foreground-700">
                        {genre}
                      </span>
                    ))}
                    <span className="rounded-full bg-secondary-100 px-2 py-0.5 text-[11px] text-secondary-900">
                      {movie.cinemaStatus}
                    </span>
                  </span>
                </span>
              </article>
            ))}
          </div>
        ) : (
          <div className="mt-6 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 py-14 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-200 text-foreground-600">
              <i className="ri-search-eye-line text-2xl" aria-hidden="true"></i>
            </span>
            <p className="mt-3 font-heading text-base font-bold text-foreground-900">没有找到匹配的作品</p>
            <p className="mt-1 text-sm text-foreground-600">试试更换类型标签，或减少关键词后再搜索。</p>
            <button
              type="button"
              onClick={() => {
                setActiveGenre('全部');
                setQuery('');
              }}
              className="mt-4 cursor-pointer whitespace-nowrap rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700"
            >
              重置筛选条件
            </button>
          </div>
        )}
      </div>
    </section>
  );
}